import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  IconButton,
  Input,
  Stack,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/joy';
import {
  Plus as Add,
  X as Close,
  Trash as Delete,
  DotsSixVertical as DragIndicator,
  CaretDown as ExpandMore,
  SquaresFour as Dashboard,
} from '@phosphor-icons/react';
import {
  DragDropContext,
  Droppable,
  Draggable,
} from '@hello-pangea/dnd';
import type { DropResult, DroppableProvided, DraggableProvided, DraggableStateSnapshot } from '@hello-pangea/dnd';
import { DraggablePortal } from '../../common/DraggablePortal';
import { useDebouncedRename } from '../../../hooks/useDebouncedRename';
import {
  useOpportunityBoards,
  useOpportunityBoardStatuses,
  useCreateOpportunityBoard,
  useCreateOpportunityBoardStatus,
  useDeleteOpportunityBoard,
  useDeleteOpportunityBoardStatus,
  useUpdateOpportunityBoardStatus,
} from '../../../hooks/useOpportunityBoards';
import type { OpportunityBoard, OpportunityBoardStatus } from '../../../hooks/useOpportunityBoards';

const DEFAULT_STAGES = [
  'New Lead',
  'Contacted',
  'Qualified',
  'Proposal Sent',
  'Negotiation',
];

interface Stage {
  id: number | string;
  name: string;
  order: number;
  isNew?: boolean;
}

interface BoardWithStages {
  board: OpportunityBoard;
  stages: Stage[];
  newStageName: string;
  isExpanded: boolean;
}

type OpportunityBoardsStepProps = Record<string, never>;

export const OpportunityBoardsStep: React.FC<OpportunityBoardsStepProps> = () => {
  const { data: boards, isLoading: loadingBoards, refetch: refetchBoards } = useOpportunityBoards();
  const createBoard = useCreateOpportunityBoard();
  const deleteBoard = useDeleteOpportunityBoard();
  const createStatus = useCreateOpportunityBoardStatus();
  const deleteStatus = useDeleteOpportunityBoardStatus();
  const updateStatus = useUpdateOpportunityBoardStatus();

  const [boardsWithStages, setBoardsWithStages] = useState<BoardWithStages[]>([]);
  const [newBoardName, setNewBoardName] = useState('');
  const [isAddingBoard, setIsAddingBoard] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);

  // Initialize boards with stages
  useEffect(() => {
    if (!loadingBoards && boards && !initialized) {
      if (boards.length === 0) {
        // Create default board
        createDefaultBoard();
      } else {
        // Initialize with existing boards
        setBoardsWithStages(
          boards.map((board: OpportunityBoard) => ({
            board,
            stages: [],
            newStageName: '',
            isExpanded: true,
          }))
        );
      }
      setInitialized(true);
    }
  }, [loadingBoards, boards, initialized]);

  const createDefaultBoard = async () => {
    try {
      const newBoard = await createBoard.mutateAsync({ name: 'Sales Pipeline', color: '#FFFFFF' });
      // Create default stages
      for (let i = 0; i < DEFAULT_STAGES.length; i++) {
        await createStatus.mutateAsync({
          board: newBoard.id,
          name: DEFAULT_STAGES[i],
          order: i,
        });
      }
      refetchBoards();
    } catch (err) {
      console.error('Failed to create default board:', err);
    }
  };

  const handleAddBoard = async () => {
    if (!newBoardName.trim()) return;

    setIsAddingBoard(true);
    try {
      const newBoard = await createBoard.mutateAsync({ name: newBoardName.trim(), color: '#FFFFFF' });
      // Create default stages for new board
      for (let i = 0; i < DEFAULT_STAGES.length; i++) {
        await createStatus.mutateAsync({
          board: newBoard.id,
          name: DEFAULT_STAGES[i],
          order: i,
        });
      }
      setNewBoardName('');
      refetchBoards();
      setInitialized(false); // Re-initialize to pick up new board
    } catch (err: any) {
      setError(err?.message || 'Failed to create board');
    } finally {
      setIsAddingBoard(false);
    }
  };

  const handleDeleteBoard = async (boardId: number) => {
    try {
      await deleteBoard.mutateAsync(boardId);
      setBoardsWithStages((prev) => prev.filter((b) => b.board.id !== boardId));
      refetchBoards();
    } catch (err: any) {
      setError(err?.message || 'Failed to delete board. It may have opportunities assigned.');
    }
  };

  if (loadingBoards) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert
          color="danger"
          sx={{ mb: 2 }}
          endDecorator={
            <IconButton variant="soft" color="danger" size="sm" onClick={() => setError(null)}>
              <Close />
            </IconButton>
          }
        >
          {error}
        </Alert>
      )}

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography level="title-md">Your Boards</Typography>
      </Box>

      <Typography level="body-sm" sx={{ mb: 3, color: 'neutral.600' }}>
        Create boards for different types of sales opportunities (e.g., Residential,
        Commercial, Insurance Claims). Each board has its own stages.
      </Typography>

      {/* Existing Boards */}
      <Stack spacing={2} sx={{ mb: 3 }}>
        {boards?.map((board: OpportunityBoard) => (
          <BoardCard
            key={board.id}
            board={board}
            onDelete={() => handleDeleteBoard(board.id)}
            canDelete={(boards?.length || 0) > 1}
          />
        ))}
      </Stack>

      {/* Add New Board */}
      <Card variant="outlined" sx={{ p: 2, backgroundColor: 'background.level1' }}>
        <Typography level="title-sm" sx={{ mb: 1.5 }}>
          Add Another Board
        </Typography>
        <Stack direction="row" spacing={1}>
          <Input
            placeholder="Board name (e.g., Insurance Claims)"
            value={newBoardName}
            onChange={(e) => setNewBoardName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAddBoard()}
            sx={{ flex: 1 }}
            size="sm"
          />
          <Button
            variant="soft"
            startDecorator={<Add />}
            onClick={handleAddBoard}
            disabled={!newBoardName.trim() || isAddingBoard}
            loading={isAddingBoard}
            size="sm"
          >
            Add Board
          </Button>
        </Stack>
      </Card>

    </Box>
  );
};

// Separate component for each board card with its own stages
interface BoardCardProps {
  board: OpportunityBoard;
  onDelete: () => void;
  canDelete: boolean;
}

const BoardCard: React.FC<BoardCardProps> = ({ board, onDelete, canDelete }) => {
  const { data: existingStatuses, isLoading: loadingStatuses, refetch } = useOpportunityBoardStatuses(board.id);
  const createStatus = useCreateOpportunityBoardStatus();
  const deleteStatus = useDeleteOpportunityBoardStatus();
  const updateStatus = useUpdateOpportunityBoardStatus();

  const [stages, setStages] = useState<Stage[]>([]);
  const [newStageName, setNewStageName] = useState('');
  const [isExpanded, setIsExpanded] = useState(true);

  const rename = useDebouncedRename((id, name) =>
    updateStatus.mutateAsync({ id, data: { name } })
  );

  // Initialize stages from existing data
  useEffect(() => {
    if (existingStatuses && existingStatuses.length > 0) {
      setStages(
        existingStatuses.map((s: OpportunityBoardStatus, idx: number) => ({
          id: s.id,
          // Keep any rename the user has typed but not saved yet — this data
          // may predate their last few keystrokes.
          name: rename.resolve(s.id, s.name),
          order: s.order ?? idx,
        }))
      );
    }
  }, [existingStatuses, rename]);

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination) return;

    const items = Array.from(stages);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update order numbers
    const reorderedStages = items.map((item, idx) => ({
      ...item,
      order: idx,
    }));

    setStages(reorderedStages);

    // Save order changes to backend
    try {
      for (const stage of reorderedStages) {
        if (typeof stage.id === 'number') {
          await updateStatus.mutateAsync({
            id: stage.id,
            data: { order: stage.order },
          });
        }
      }
    } catch (err) {
      console.error('Failed to update order:', err);
    }
  };

  const handleAddStage = async () => {
    if (!newStageName.trim()) return;

    try {
      await createStatus.mutateAsync({
        board: board.id,
        name: newStageName.trim(),
        order: stages.length,
      });
      setNewStageName('');
      refetch();
    } catch (err) {
      console.error('Failed to add stage:', err);
    }
  };

  const handleRemoveStage = async (stageId: number | string) => {
    if (typeof stageId === 'number') {
      try {
        await deleteStatus.mutateAsync({ id: stageId, boardId: board.id });
        refetch();
      } catch (err) {
        console.error('Failed to delete stage:', err);
      }
    }
  };

  const handleRenameStage = (stageId: number | string, newName: string) => {
    setStages((prev) =>
      prev.map((s) => (s.id === stageId ? { ...s, name: newName } : s))
    );
    rename.queue(stageId, newName);
  };

  return (
    <Card variant="outlined">
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: isExpanded ? '1px solid' : 'none',
          borderColor: 'divider',
          cursor: 'pointer',
        }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Dashboard color="var(--joy-palette-primary-500)" />
          <Typography level="title-md">{board.name}</Typography>
          <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
            ({stages.length} stages)
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {canDelete && (
            <IconButton
              variant="plain"
              color="danger"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
            >
              <Delete />
            </IconButton>
          )}
          <IconButton variant="plain" size="sm">
            <ExpandMore
              style={{
                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s',
              }}
            />
          </IconButton>
        </Box>
      </Box>

      {isExpanded && (
        <Box sx={{ p: 2 }}>
          {loadingStatuses ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size="sm" />
            </Box>
          ) : (
            <>
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId={`board-${board.id}`}>
                  {(provided: DroppableProvided) => (
                    <Stack
                      spacing={1}
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      sx={{ mb: 2 }}
                    >
                      {stages.map((stage, index) => (
                        <Draggable
                          key={stage.id}
                          draggableId={`stage-${board.id}-${stage.id}`}
                          index={index}
                        >
                          {(provided: DraggableProvided, snapshot: DraggableStateSnapshot) => (
                            <DraggablePortal snapshot={snapshot}>
                              <Box
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  p: 1,
                                  borderRadius: 'sm',
                                  border: '1px solid',
                                  borderColor: snapshot.isDragging
                                    ? 'primary.300'
                                    : 'neutral.200',
                                  backgroundColor: snapshot.isDragging
                                    ? 'primary.50'
                                    : 'background.surface',
                                }}
                              >
                                <Box
                                  {...provided.dragHandleProps}
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    color: 'neutral.500',
                                    cursor: 'grab',
                                  }}
                                >
                                  <DragIndicator size={18} />
                                </Box>
                                <Input
                                  value={stage.name}
                                  onChange={(e) =>
                                    handleRenameStage(stage.id, e.target.value)
                                  }
                                  sx={{ flex: 1 }}
                                  size="sm"
                                  variant="plain"
                                />
                                <IconButton
                                  variant="plain"
                                  color="danger"
                                  size="sm"
                                  onClick={() => handleRemoveStage(stage.id)}
                                  disabled={stages.length <= 2}
                                >
                                  <Delete size={18} />
                                </IconButton>
                              </Box>
                            </DraggablePortal>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </Stack>
                  )}
                </Droppable>
              </DragDropContext>

              {/* Add new stage */}
              <Stack direction="row" spacing={1}>
                <Input
                  placeholder="Add a new stage..."
                  value={newStageName}
                  onChange={(e) => setNewStageName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddStage()}
                  sx={{ flex: 1 }}
                  size="sm"
                />
                <Button
                  variant="plain"
                  startDecorator={<Add />}
                  onClick={handleAddStage}
                  disabled={!newStageName.trim()}
                  size="sm"
                >
                  Add Stage
                </Button>
              </Stack>
            </>
          )}
        </Box>
      )}
    </Card>
  );
};

export default OpportunityBoardsStep;
